import { useMemo, useState } from "react";

/**
 * useCalendarFilters
 *
 * Centralises the calendar page's two interactive filter Sets
 * (child chips + parent chips) and exposes the `visibleEvents` memo
 * derived from them. Extracted from pages/CalendarPage.jsx in the
 * v77.99.19 Phase 3 frontend decomposition pass (slice 2 — Calendar)
 * to satisfy the "preserve child-specific filtering isolation"
 * requirement: filter state and filter logic now live together so a
 * future refactor of the legend UI can't accidentally drift from the
 * predicate that drives the grid.
 *
 * Behaviour preserved verbatim from the inline original:
 *   - When the global ChildScope is locked to a single child
 *     (`selectedChildId` truthy), the chip-based child filter Set is
 *     ignored. The API already returns only that child's events, and
 *     a stale Set from an earlier "All children" session would
 *     otherwise silently filter every event out.
 *   - Parent filter is mutually compatible with child filter (AND
 *     semantics).
 *   - Belt-and-braces: when filtering by a co-parent, hide their
 *     private entries even if the API ever leaks them.
 *
 * Args:
 *  - events:           Event[]
 *  - selectedChildId:  string | null  (from ChildScopeContext)
 *  - userId:           string | null  (current user's id)
 *
 * Returns:
 *  {
 *    childFilter,    setChildFilter,
 *    parentFilter,   setParentFilter,
 *    visibleEvents,
 *    hasActiveFilters,
 *    clearAll,
 *    toggleChild(id),
 *    toggleParent(id),
 *  }
 */
export function useCalendarFilters(events, selectedChildId, userId) {
  const [childFilter, setChildFilter] = useState(() => new Set());
  const [parentFilter, setParentFilter] = useState(() => new Set());

  const visibleEvents = useMemo(() => {
    const childOn = !selectedChildId && childFilter.size > 0;
    const parentOn = parentFilter.size > 0;
    if (!childOn && !parentOn) return events;
    return events.filter((ev) => {
      if (childOn && !(ev.child_ids || []).some((id) => childFilter.has(id))) return false;
      if (parentOn) {
        if (!parentFilter.has(ev.parent_id)) return false;
        if (ev.parent_id !== userId && !ev.is_shared) return false;
      }
      return true;
    });
  }, [events, childFilter, parentFilter, userId, selectedChildId]);

  const toggleChild = (id) => setChildFilter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleParent = (id) => setParentFilter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const clearAll = () => {
    setChildFilter(new Set());
    setParentFilter(new Set());
  };

  return {
    childFilter, setChildFilter,
    parentFilter, setParentFilter,
    visibleEvents,
    hasActiveFilters: childFilter.size > 0 || parentFilter.size > 0,
    clearAll,
    toggleChild,
    toggleParent,
  };
}
